import { useRef, type PointerEvent } from 'react'
import type { MobileAdapter } from '@/input/MobileAdapter'

interface Props {
  adapter: MobileAdapter
  leftHand: boolean
  disabled?: boolean
}

/** Anti-select / callout при hold Defuse на mobile. */
function blockBrowserChrome(e: PointerEvent<HTMLButtonElement>): void {
  e.preventDefault()
}

export function MobileControls({ adapter, leftHand, disabled }: Props) {
  const stickRef = useRef<HTMLDivElement>(null)
  const knobRef = useRef<HTMLDivElement>(null)
  const lookId = useRef<number | null>(null)
  const lastLook = useRef<{ x: number; y: number } | null>(null)

  const onStickStart = (e: PointerEvent<HTMLDivElement>) => {
    if (disabled) return
    e.currentTarget.setPointerCapture(e.pointerId)
    moveStick(e)
  }

  const moveStick = (e: PointerEvent<HTMLDivElement>) => {
    const base = stickRef.current
    const knob = knobRef.current
    if (!base || !knob) return
    const rect = base.getBoundingClientRect()
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2
    let dx = e.clientX - cx
    let dy = e.clientY - cy
    const max = rect.width * 0.35
    const len = Math.hypot(dx, dy)
    if (len > max) {
      dx = (dx / len) * max
      dy = (dy / len) * max
    }
    knob.style.left = `${rect.width / 2 + dx - 28}px`
    knob.style.top = `${rect.height / 2 + dy - 28}px`
    adapter.state.move = { x: dx / max, y: -dy / max }
  }

  const onStickEnd = () => {
    adapter.state.move = { x: 0, y: 0 }
    const base = stickRef.current
    const knob = knobRef.current
    if (base && knob) {
      knob.style.left = `${base.clientWidth / 2 - 28}px`
      knob.style.top = `${base.clientHeight / 2 - 28}px`
    }
  }

  const onLookStart = (e: PointerEvent<HTMLDivElement>) => {
    if (disabled) return
    const t = e.target as HTMLElement
    if (t.closest('.stick-zone') || t.closest('.action-zone')) return
    lookId.current = e.pointerId
    lastLook.current = { x: e.clientX, y: e.clientY }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const onLookMove = (e: PointerEvent<HTMLDivElement>) => {
    if (lookId.current !== e.pointerId || !lastLook.current) return
    const dx = e.clientX - lastLook.current.x
    const dy = e.clientY - lastLook.current.y
    lastLook.current = { x: e.clientX, y: e.clientY }
    adapter.state.lookDelta.x += dx
    adapter.state.lookDelta.y += dy
  }

  const onLookEnd = (e: PointerEvent<HTMLDivElement>) => {
    if (lookId.current === e.pointerId) {
      lookId.current = null
      lastLook.current = null
    }
  }

  return (
    <div className={`mobile-controls${leftHand ? ' left-hand' : ''}`}>
      <div
        className="look-capture"
        onPointerDown={onLookStart}
        onPointerMove={onLookMove}
        onPointerUp={onLookEnd}
        onPointerCancel={onLookEnd}
      />

      <div className="stick-zone">
        <div
          ref={stickRef}
          className="stick-base"
          onPointerDown={onStickStart}
          onPointerMove={moveStick}
          onPointerUp={onStickEnd}
          onPointerCancel={onStickEnd}
        >
          <div ref={knobRef} className="stick-knob" />
        </div>
      </div>

      <div className="action-zone">
        <button
          type="button"
          className="touch-btn pause"
          disabled={disabled}
          onPointerDown={() => {
            adapter.state.pause = true
          }}
        >
          II
        </button>
        <button
          type="button"
          className="touch-btn"
          disabled={disabled}
          onPointerDown={() => {
            adapter.state.cameraToggle = true
          }}
        >
          Cam
        </button>
        <button
          type="button"
          className="touch-btn check"
          disabled={disabled}
          onPointerDown={() => {
            adapter.state.check = true
          }}
        >
          Check
        </button>
        <button
          type="button"
          className="touch-btn primary defuse-hold"
          disabled={disabled}
          onPointerDown={(e) => {
            blockBrowserChrome(e)
            adapter.state.defuseHold = true
          }}
          onPointerUp={() => {
            adapter.state.defuseHold = false
          }}
          onPointerCancel={() => {
            adapter.state.defuseHold = false
          }}
          onContextMenu={(e) => e.preventDefault()}
        >
          Defuse
        </button>
      </div>
    </div>
  )
}
